import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from "@/components/ui/command";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { handleUppercaseInput } from "@/lib/utils";
import { API, BACKEND_URL, apiFetch } from "@/lib/api";
import { useGarageBranding } from "@/hooks/useGarageBranding";
import { useAuth } from "@/context/AuthContext";
import { statusConfig, issuePriorityConfig, issueStatusConfig, issueCategoryConfig } from "@/lib/uiConfig";
import DTCManagementSection from "@/components/DTCManagementSection";
import WorkHoldManager from "@/components/WorkHoldManager";
import TimeTracker from "@/components/TimeTracker";
import JobCardStatusWorkflow from "@/components/JobCardStatusWorkflow";
import AddVehicleDataForm from "@/components/forms/AddVehicleDataForm";
import SimpleAddPartForm from "@/components/forms/SimpleAddPartForm";
import MasterPartForm from "@/components/forms/MasterPartForm";
import { useFormDraft, DraftRestoredBar } from "@/components/FormDraft";
import VehicleCascadeDropdowns from "@/components/VehicleCascadeDropdowns";
import {
  Plus, Car, Wrench, AlertTriangle, CheckCircle, Clock, Package,
  Search, Bell, Settings, Users, Calendar, FileText, BarChart3,
  Home, Archive, DollarSign, Phone, Mail, MapPin, Menu, X,
  AlertCircle, Bug, Zap, Settings2, Gauge, Cog, Calculator,
  Eye, Download, Camera, Edit, Trash, RefreshCw, LogOut, LogIn,
  UserPlus, Shield, PackageX, Trash2, Play, List, Clipboard, Database, Minus,
  Check, CreditCard, Truck, Send, Code, FileCheck, Activity,
  TrendingUp, TrendingDown, ArrowUpCircle, ArrowDownCircle, Loader2,
  Filter, Info, ClipboardList, ChevronDown
} from "lucide-react";

import { AddServiceForm, AddPartToPurchaseOrderForm, AddExistingItemsToInvoiceForm } from "@/pages/shared/ItemForms";


const QuotationItemRow = ({ item, onUpdate, onRemove }) => {
  return (
    <div className="flex items-center space-x-4 p-3 bg-gray-50 rounded-lg border">
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <div>
            <p className="font-medium text-sm">{item.name}</p>
            <p className="text-xs text-gray-500 capitalize">{item.type}</p>
            {item.description && (
              <p className="text-xs text-gray-600 mt-1">{item.description}</p>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <div className="text-right">
              <Label className="text-xs text-gray-500">Qty</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={item.quantity}
                onChange={(e) => onUpdate(item.id, 'quantity', parseFloat(e.target.value) || 1)}
                className="w-20 text-center text-sm"
              />
            </div>
            <div className="text-right">
              <Label className="text-xs text-gray-500">Price</Label>
              <Input
                type="number"
                min="0"
                step="0.01"
                value={item.price}
                onChange={(e) => onUpdate(item.id, 'price', parseFloat(e.target.value) || 0)}
                className="w-20 text-right text-sm"
              />
            </div>
            <div className="text-right min-w-20">
              <Label className="text-xs text-gray-500">Total</Label>
              <p className="font-semibold text-sm">₹{item.total.toLocaleString('en-IN')}</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={onRemove}
              className="text-red-600 hover:text-red-700 hover:border-red-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Edit Employee Form Component

const AddQuotationForm = ({ onQuotationAdded, onCancel, services = [], parts = [] }) => {
  const [loading, setLoading] = useState(false);
  
  // Job card selection (auto-fill customer + vehicle info)
  const [jobCards, setJobCards] = useState([]);
  const [selectedJobCardId, setSelectedJobCardId] = useState('');
  const [jobCardPopoverOpen, setJobCardPopoverOpen] = useState(false);
  
  // Cascading dropdown state for vehicle information
  const [selectedMake, setSelectedMake] = useState('');
  const [selectedModel, setSelectedModel] = useState('');
  const [selectedSeries, setSelectedSeries] = useState('');
  const [vehicleInfoMakes, setVehicleInfoMakes] = useState([]);
  const [vehicleInfoModels, setVehicleInfoModels] = useState([]);
  const [vehicleInfoSeries, setVehicleInfoSeries] = useState([]);
  
  // Services & Parts management state (same as Invoice)
  const [quotationItems, setQuotationItems] = useState([]);
  const [selectedItemType, setSelectedItemType] = useState("service");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemSearchTerm, setItemSearchTerm] = useState("");
  const [gstEnabled, setGstEnabled] = useState(true);

  useEffect(() => {
    const loadGstDefault = async () => {
      try {
        const res = await apiFetch(`${API}/settings/gst`);
        if (res.ok) {
          const data = await res.json();
          setGstEnabled(data.gst_enabled_default !== false);
        }
      } catch (e) { /* keep default true */ }
    };
    loadGstDefault();
  }, []);

  // Dialog states for Add New / Add Existing buttons (same UX as Invoice form)
  const [showAddExistingServiceDialog, setShowAddExistingServiceDialog] = useState(false);
  const [showAddNewServiceDialog, setShowAddNewServiceDialog] = useState(false);
  const [showAddExistingPartDialog, setShowAddExistingPartDialog] = useState(false);
  const [showAddNewPartDialog, setShowAddNewPartDialog] = useState(false);
  
  // Vehicle filter states for Services & Parts selection
  const [vehicleFilterMake, setVehicleFilterMake] = useState('all');
  const [vehicleFilterModel, setVehicleFilterModel] = useState('all');
  const [vehicleFilterSeries, setVehicleFilterSeries] = useState('all');
  
  // Vehicle data for filters
  const [vehicleData, setVehicleData] = useState({});
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  // Use React Hook Form for better form management and to prevent field clearing
  const { register, handleSubmit, watch, formState: { errors }, reset, setValue } = useForm({
    defaultValues: {
      customer_name: '',
      customer_email: '',
      customer_phone: '',
      vehicle_make: '',
      vehicle_model: '',
      vehicle_series: '',
      vehicle_year: '',
      vehicle_registration: '',
      vin: '',
      labor_hours: '',
      labor_rate: '800', // Default labor rate per hour in INR
      notes: '',
      valid_days: '30' // Default validity period
    }
  });

  // Watch values for real-time calculations (updated to use quotationItems)
  const watchedValues = watch(['labor_hours', 'labor_rate']);

  // Autosave draft (create only)
  const quoRhfValues = watch();
  const quoDraftHasContent =
    quotationItems.length > 0 ||
    (quoRhfValues.customer_name || '').trim() !== '' ||
    (quoRhfValues.customer_phone || '').trim() !== '' ||
    (quoRhfValues.notes || '').trim() !== '' ||
    !!selectedJobCardId;
  const { draftRestored, clearDraft, dismissBanner } = useFormDraft({
    storageKey: 'draft:quotation-create',
    hasContent: quoDraftHasContent,
    data: { rhf: quoRhfValues, quotationItems, selectedJobCardId, selectedMake, selectedModel, selectedSeries },
    onRestore: (d) => {
      if (d.rhf) reset(d.rhf);
      if (Array.isArray(d.quotationItems)) setQuotationItems(d.quotationItems);
      if (d.selectedJobCardId) setSelectedJobCardId(d.selectedJobCardId);
      if (d.selectedMake) setSelectedMake(d.selectedMake);
      if (d.selectedModel) setSelectedModel(d.selectedModel);
      if (d.selectedSeries) setSelectedSeries(d.selectedSeries);
    },
  });
  const discardQuotationDraft = () => {
    clearDraft();
    reset();
    setQuotationItems([]);
    setSelectedJobCardId('');
    setSelectedMake(''); setSelectedModel(''); setSelectedSeries('');
  };

  // Fetch vehicle makes for Vehicle Information section (from vehicle_data)
  useEffect(() => {
    const fetchInfoMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setVehicleInfoMakes(makes.sort());
      } catch (error) {
        setVehicleInfoMakes([]);
      }
    };
    fetchInfoMakes();
  }, []);

  // Fetch job cards for the "Job Card Number" selector
  useEffect(() => {
    const fetchJobCards = async () => {
      try {
        const response = await axios.get(`${API}/jobcards`);
        setJobCards(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Failed to load job cards', error);
        setJobCards([]);
      }
    };
    fetchJobCards();
  }, []);

  // Auto-fill customer + vehicle details from the selected job card
  const handleJobCardSelect = (jobCardId) => {
    setSelectedJobCardId(jobCardId);
    const jc = jobCards.find((j) => j.id === jobCardId);
    if (!jc) return;
    // Customer information
    setValue('customer_name', jc.customer_name || '');
    setValue('customer_email', jc.customer_email || '');
    setValue('customer_phone', jc.customer_contact || '');
    // Vehicle registration & VIN
    setValue('vehicle_registration', jc.vehicle_reg || '');
    setValue('vin', jc.vin || '');
    // Vehicle make/model/series/year (cascading dropdowns + form values)
    const mk = jc.vehicle_make || '';
    const md = jc.vehicle_model || '';
    const sr = jc.vehicle_series || '';
    setSelectedMake(mk);
    setSelectedModel(md);
    setSelectedSeries(sr);
    setValue('vehicle_make', mk);
    setValue('vehicle_model', md);
    setValue('vehicle_series', sr);
    setValue('vehicle_year', jc.year ? String(jc.year) : '');
  };


  // Cascade: fetch models when Vehicle Info make changes
  useEffect(() => {
    if (!selectedMake || selectedMake === 'Other') {
      setVehicleInfoModels([]);
      return;
    }
    const fetchInfoModels = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        setVehicleInfoModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) {
        setVehicleInfoModels([]);
      }
    };
    fetchInfoModels();
  }, [selectedMake]);

  // Cascade: fetch series when Vehicle Info model changes
  useEffect(() => {
    if (!selectedModel || !selectedMake || selectedModel === 'Other Model') {
      setVehicleInfoSeries([]);
      return;
    }
    const fetchInfoSeries = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        setVehicleInfoSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
      } catch (error) {
        setVehicleInfoSeries([]);
      }
    };
    fetchInfoSeries();
  }, [selectedModel, selectedMake]);


  // Calculate totals from quotation items
  const calculateTotal = () => {
    const laborCost = (parseFloat(watchedValues[0]) || 0) * (parseFloat(watchedValues[1]) || 0);
    const itemsTotal = quotationItems.reduce((sum, item) => sum + item.total, 0);
    return laborCost + itemsTotal;
  };
  
  // Fetch vehicle data for item filtering (from vehicle_data collection)
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
        setAvailableMakes(makes.sort());
      } catch (error) {
        setAvailableMakes([]);
      }
    };
    fetchVehicleMakes();
  }, []);
  
  // Update available models when make filter changes
  useEffect(() => {
    if (vehicleFilterMake === 'all') {
      setAvailableModels([]);
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchModels = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(vehicleFilterMake)}`);
          setAvailableModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
        } catch (error) {
          setAvailableModels([]);
        }
      };
      fetchModels();
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterMake]);
  
  // Update available series when model filter changes
  useEffect(() => {
    if (vehicleFilterModel === 'all') {
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchSeries = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(vehicleFilterMake)}/${encodeURIComponent(vehicleFilterModel)}`);
          setAvailableSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
        } catch (error) {
          setAvailableSeries([]);
        }
      };
      fetchSeries();
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterModel, vehicleFilterMake]);

  // Get available items based on selected type (same as Invoice)
  const getAvailableItems = () => {
    let items = [];
    
    if (selectedItemType === "service") {
      items = services || [];
    } else {
      items = parts || [];
    }
    
    // Apply vehicle compatibility filters if any are selected
    if (vehicleFilterMake !== 'all' || vehicleFilterModel !== 'all' || vehicleFilterSeries !== 'all') {
      items = items.filter(item => {
        // If item has no compatibility data, show it in all filters (backward compatibility)
        if (!item.compatible_makes || item.compatible_makes.length === 0) {
          return true;
        }
        
        // Check make compatibility
        const makeMatches = vehicleFilterMake === 'all' || 
          item.compatible_makes.includes(vehicleFilterMake);
        
        // Check model compatibility  
        const modelMatches = vehicleFilterModel === 'all' || 
          !item.compatible_models || 
          item.compatible_models.length === 0 || 
          item.compatible_models.includes(vehicleFilterModel);
        
        // Check series compatibility
        const seriesMatches = vehicleFilterSeries === 'all' || 
          !item.compatible_series || 
          item.compatible_series.length === 0 || 
          item.compatible_series.includes(vehicleFilterSeries);
        
        return makeMatches && modelMatches && seriesMatches;
      });
    }
    
    return items;
  };

  // Add item to quotation (same as Invoice)
  const addQuotationItem = () => {
    if (!selectedItemId) {
      alert("Please select an item");
      return;
    }

    const availableItems = getAvailableItems();
    const selectedItem = availableItems.find(item => item.id === selectedItemId);
    
    if (!selectedItem) {
      alert("Selected item not found");
      return;
    }

    const isPart = selectedItemType === 'part';
    const pb = isPart && Array.isArray(selectedItem.brands) && selectedItem.brands.length > 0 ? selectedItem.brands[0] : null;
    const price = pb ? (parseFloat(pb.selling_price) || parseFloat(pb.cost_price) || 0) : (selectedItem.price || selectedItem.selling_price || selectedItem.cost_price || 0);
    const total = price * itemQuantity;

    const newItem = {
      id: `${selectedItemType}_${selectedItemId}_${Date.now()}`,
      type: selectedItemType,
      item_id: selectedItemId,
      name: selectedItem.name || selectedItem.part_name || selectedItem.description,
      price: price,
      quantity: itemQuantity,
      total: total,
      description: selectedItem.description || '',
      brand_id: pb ? pb.id : null,
      brand_name: pb ? pb.brand_name : null
    };

    setQuotationItems(prev => [...prev, newItem]);
    setSelectedItemId("");
    setItemQuantity(1);
  };

  // Remove item from quotation
  const removeQuotationItem = (itemId) => {
    setQuotationItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Update item in quotation
  const updateQuotationItem = (itemId, field, value) => {
    setQuotationItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'price') {
          updatedItem.total = updatedItem.quantity * updatedItem.price;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const onSubmit = (data) => {
    setLoading(true);

    try {
      if (!data.customer_name.trim()) {
        alert('Please enter customer name');
        return;
      }

      // Check for required vehicle information using cascading dropdown values
      if (!selectedMake.trim() || !selectedModel.trim()) {
        alert('Please select vehicle make and model');
        return;
      }

      // Construct vehicle information from cascading dropdown values
      const vehicleInfo = [
        selectedMake.trim(),
        selectedModel.trim(), 
        selectedSeries.trim(),
        data.vehicle_year?.trim()
      ].filter(Boolean).join(' ');

      const phoneValue = (data.customer_phone || '').trim();
      const quotationData = {
        customer_name: data.customer_name.trim(),
        customer_email: data.customer_email.trim(),
        customer_phone: phoneValue,
        customer_contact: phoneValue, // Backend uses customer_contact field
        vehicle_info: vehicleInfo, // Constructed from cascading dropdown values
        vehicle_make: selectedMake.trim(),
        vehicle_model: selectedModel.trim(),
        vehicle_series: selectedSeries.trim() || null,
        vehicle_year: data.vehicle_year.trim(),
        vehicle_registration: (data.vehicle_registration || '').trim() || null,
        vin: (data.vin || '').trim() || null,
        job_card_id: selectedJobCardId || null,
        job_number: (jobCards.find((j) => j.id === selectedJobCardId) || {}).job_number || null,
        items: quotationItems, // Using quotationItems instead of services/parts text
        labor_hours: parseFloat(data.labor_hours) || 0,
        labor_rate: parseFloat(data.labor_rate) || 0,
        total_amount: calculateTotal(),
        notes: data.notes.trim(),
        gst_enabled: gstEnabled,
        valid_until: new Date(Date.now() + (parseInt(data.valid_days) * 24 * 60 * 60 * 1000)).toISOString().split('T')[0]
      };

      onQuotationAdded(quotationData);
      clearDraft();
      
      // Reset form and cascading dropdowns after successful submission
      reset();
      setQuotationItems([]);
      setSelectedJobCardId('');
      setSelectedMake('');
      setSelectedModel('');
      setSelectedSeries('');
    } catch (error) {
      console.error('Error creating quotation:', error);
      alert('An error occurred while creating the quotation.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {draftRestored && <DraftRestoredBar onDiscard={discardQuotationDraft} onDismiss={dismissBanner} />}
      {/* Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Job Card selector — auto-fills customer & vehicle details */}
          <div className="rounded-md border border-blue-200 bg-blue-50/60 p-3">
            <Label htmlFor="job_card_select" className="text-sm font-medium">Job Card Number (optional)</Label>
            <Popover open={jobCardPopoverOpen} onOpenChange={setJobCardPopoverOpen}>
              <PopoverTrigger asChild>
                <Button
                  type="button"
                  variant="outline"
                  role="combobox"
                  aria-expanded={jobCardPopoverOpen}
                  className="w-full justify-between bg-white mt-1 font-normal"
                  data-testid="quotation-jobcard-select"
                >
                  <span className="truncate text-left">
                    {selectedJobCardId
                      ? (() => {
                          const jc = jobCards.find((j) => j.id === selectedJobCardId);
                          return jc ? `${jc.job_number} — ${jc.customer_name || 'N/A'} (${jc.vehicle_reg || jc.vehicle_make || 'Vehicle'})` : 'Select a job card...';
                        })()
                      : 'Select a job card to auto-fill customer & vehicle details'}
                  </span>
                  <ChevronDown className="h-4 w-4 shrink-0 opacity-50" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                <Command>
                  <CommandInput placeholder="Search by job number, customer or vehicle..." data-testid="quotation-jobcard-search-input" />
                  <CommandList className="max-h-60">
                    <CommandEmpty>No job card found.</CommandEmpty>
                    <CommandGroup>
                      {jobCards.map((jc) => (
                        <CommandItem
                          key={jc.id}
                          value={`${jc.job_number} ${jc.customer_name || ''} ${jc.vehicle_reg || ''} ${jc.vehicle_make || ''} ${jc.vehicle_model || ''}`}
                          onSelect={() => {
                            handleJobCardSelect(jc.id);
                            setJobCardPopoverOpen(false);
                          }}
                          data-testid={`quotation-jobcard-option-${jc.id}`}
                        >
                          <Check className={`mr-2 h-4 w-4 ${selectedJobCardId === jc.id ? 'opacity-100' : 'opacity-0'}`} />
                          {jc.job_number} — {jc.customer_name || 'N/A'} ({jc.vehicle_reg || jc.vehicle_make || 'Vehicle'})
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  </CommandList>
                </Command>
              </PopoverContent>
            </Popover>
            <p className="text-xs text-gray-500 mt-1">Fills customer info, vehicle make/model, registration number and VIN from the job card.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">Customer Name *</Label>
              <Input
                id="customer_name"
                {...register("customer_name", { 
                  required: "Customer name is required",
                  minLength: { value: 2, message: "Name must be at least 2 characters" }
                })}
                placeholder="Enter customer name"
              />
              {errors.customer_name && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="customer_email">Email</Label>
              <Input
                id="customer_email"
                type="email"
                {...register("customer_email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address"
                  }
                })}
                placeholder="customer@example.com"
              />
              {errors.customer_email && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_email.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_phone">Phone Number</Label>
              <Input
                id="customer_phone"
                {...register("customer_phone")}
                placeholder="+91-9876543210"
              />
            </div>
          </div>

          {/* Vehicle Information - Cascading Dropdowns */}
          <div>
            <Label className="text-base font-semibold">🚗 Vehicle Information</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
              {/* MAKE Dropdown */}
              <div>
                <Label htmlFor="vehicle_make">MAKE *</Label>
                <Select
                  value={selectedMake}
                  onValueChange={(value) => {
                    setSelectedMake(value);
                    setValue('vehicle_make', value);
                    // Reset model and series when make changes
                    setSelectedModel('');
                    setSelectedSeries('');
                    setValue('vehicle_model', '');
                    setValue('vehicle_series', '');
                  }}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select vehicle MAKE" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {selectedMake && selectedMake !== 'Other' && !vehicleInfoMakes.includes(selectedMake) && (
                      <SelectItem key={selectedMake} value={selectedMake}>{selectedMake}</SelectItem>
                    )}
                    {vehicleInfoMakes.map((make) => (
                      <SelectItem key={make} value={make}>{make}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {!selectedMake && (
                  <p className="text-sm text-red-600 mt-1">Vehicle make is required</p>
                )}
              </div>

              {/* MODEL Dropdown */}
              <div>
                <Label htmlFor="vehicle_model">MODEL *</Label>
                <Select
                  value={selectedModel}
                  onValueChange={(value) => {
                    setSelectedModel(value);
                    setValue('vehicle_model', value);
                    // Reset series when model changes
                    setSelectedSeries('');
                    setValue('vehicle_series', '');
                  }}
                  disabled={!selectedMake}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue 
                      placeholder={
                        selectedMake 
                          ? `Select ${selectedMake} MODEL` 
                          : "Select MAKE first"
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {selectedModel && selectedModel !== 'Other Model' && !vehicleInfoModels.includes(selectedModel) && (
                      <SelectItem key={selectedModel} value={selectedModel}>{selectedModel}</SelectItem>
                    )}
                    {vehicleInfoModels.length > 0 ? (
                      vehicleInfoModels.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))
                    ) : selectedMake ? (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        No predefined models for {selectedMake}
                      </div>
                    ) : null}
                    {selectedMake && selectedMake !== 'Other' && (
                      <SelectItem value="Other Model">Other {selectedMake} Model</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedMake && (
                  <div className="text-xs text-gray-500 mt-1">
                    {vehicleInfoModels.length > 0 
                      ? `${vehicleInfoModels.length} ${selectedMake} MODELs available`
                      : `Select ${selectedMake} MODEL`
                    }
                  </div>
                )}
                {!selectedModel && selectedMake && (
                  <p className="text-sm text-red-600 mt-1">Vehicle model is required</p>
                )}
              </div>

              {/* SERIES Dropdown */}
              <div>
                <Label htmlFor="vehicle_series">SERIES</Label>
                <Select
                  value={selectedSeries}
                  onValueChange={(value) => {
                    setSelectedSeries(value);
                    setValue('vehicle_series', value);
                  }}
                  disabled={!selectedMake || !selectedModel}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue 
                      placeholder={
                        !selectedMake ? "Select MAKE first" :
                        !selectedModel ? "Select MODEL first" :
                        `Select ${selectedModel} SERIES`
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {selectedSeries && selectedSeries !== 'Other Series' && !vehicleInfoSeries.includes(selectedSeries) && (
                      <SelectItem key={selectedSeries} value={selectedSeries}>{selectedSeries}</SelectItem>
                    )}
                    {vehicleInfoSeries.length > 0 ? (
                      vehicleInfoSeries.map((series) => (
                        <SelectItem key={series} value={series}>
                          {series}
                        </SelectItem>
                      ))
                    ) : selectedMake && selectedModel ? (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        No predefined series for {selectedModel}
                      </div>
                    ) : null}
                    {selectedMake && selectedModel && selectedModel !== 'Other Model' && (
                      <SelectItem value="Other Series">Other {selectedModel} Series</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedMake && selectedModel && (
                  <div className="text-xs text-gray-500 mt-1">
                    {vehicleInfoSeries.length > 0 
                      ? `${vehicleInfoSeries.length} ${selectedModel} SERIES available`
                      : `Select ${selectedModel} SERIES`
                    }
                  </div>
                )}
                {!selectedMake || !selectedModel ? (
                  <p className="text-xs text-gray-500 mt-1">Optional: Select MAKE and MODEL first to see SERIES options</p>
                ) : null}
              </div>

              {/* Year Field (unchanged) */}
              <div>
                <Label htmlFor="vehicle_year">Year</Label>
                <Input
                  id="vehicle_year"
                  type="number"
                  min="1980"
                  max="2030"
                  {...register("vehicle_year")}
                  placeholder="e.g., 2020"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Model year</p>
              </div>
              {/* Vehicle Registration Number (auto-filled from job card) */}
              <div>
                <Label htmlFor="vehicle_registration">Registration Number</Label>
                <Input
                  id="vehicle_registration"
                  {...register("vehicle_registration")}
                  placeholder="e.g., KL-07-AB-1234"
                  data-testid="quotation-vehicle-registration"
                />
                <p className="text-xs text-gray-500 mt-1">License plate (from job card)</p>
              </div>
              {/* VIN Number (auto-filled from job card) */}
              <div>
                <Label htmlFor="vin">VIN Number</Label>
                <Input
                  id="vin"
                  {...register("vin")}
                  placeholder="Vehicle Identification Number"
                  data-testid="quotation-vin"
                />
                <p className="text-xs text-gray-500 mt-1">Chassis / VIN (from job card)</p>
              </div>

            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services & Parts */}
      <Card>
        <CardHeader>
          <div className="flex flex-wrap justify-between items-center gap-2">
            <CardTitle>Services & Parts</CardTitle>
            <div className="flex flex-wrap gap-2">
              <Button
                type="button"
                onClick={() => setShowAddNewServiceDialog(true)}
                variant="outline"
                size="sm"
                className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                data-testid="quotation-add-new-service-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Service
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddExistingServiceDialog(true)}
                variant="outline"
                size="sm"
                data-testid="quotation-add-existing-service-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Existing Service
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddNewPartDialog(true)}
                variant="outline"
                size="sm"
                className="bg-green-50 hover:bg-green-100 text-green-700 border-green-300"
                data-testid="quotation-add-new-part-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add New Part
              </Button>
              <Button
                type="button"
                onClick={() => setShowAddExistingPartDialog(true)}
                variant="outline"
                size="sm"
                data-testid="quotation-add-existing-part-btn"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Existing Part
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Added Items Display */}
          <div className="space-y-2">
            {quotationItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                <Package className="h-8 w-8 mx-auto mb-2" />
                <p>No items added yet</p>
                <p className="text-sm">Click the buttons above to add services or parts</p>
              </div>
            ) : (
              quotationItems.map(item => (
                <QuotationItemRow 
                  key={item.id} 
                  item={item} 
                  onUpdate={updateQuotationItem} 
                  onRemove={() => removeQuotationItem(item.id)} 
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Pricing (Indian Rupees)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="labor_hours">Additional Labor Hours</Label>
              <Input
                id="labor_hours"
                type="number"
                step="0.5"
                min="0"
                {...register("labor_hours")}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Extra labor beyond service items</p>
            </div>
            <div>
              <Label htmlFor="labor_rate">Labor Rate (₹/hour)</Label>
              <Input
                id="labor_rate"
                type="number"
                min="0"
                {...register("labor_rate")}
                placeholder="800"
              />
            </div>
            <div>
              <Label htmlFor="valid_days">Valid for (days)</Label>
              <Input
                id="valid_days"
                type="number"
                min="1"
                {...register("valid_days")}
                placeholder="30"
              />
            </div>
          </div>

          {/* Total Calculation */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-blue-200">
              <div>
                <Label htmlFor="quotation-gst-toggle" className="font-medium">Apply GST</Label>
                <p className="text-xs text-muted-foreground">CGST+SGST or IGST added automatically to the total.</p>
              </div>
              <Switch id="quotation-gst-toggle" data-testid="quotation-gst-toggle" checked={gstEnabled} onCheckedChange={setGstEnabled} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Items Total:</span>
                <span>₹{quotationItems.reduce((sum, item) => sum + item.total, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Additional Labor Cost:</span>
                <span>₹{((parseFloat(watchedValues[0]) || 0) * (parseFloat(watchedValues[1]) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              {gstEnabled && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>GST:</span>
                  <span>Calculated on save (CGST+SGST or IGST)</span>
                </div>
              )}
              <hr className="border-blue-200" />
              <div className="flex justify-between text-lg font-bold text-blue-700">
                <span>Total Amount{gstEnabled ? ' (excl. GST, shown after saving)' : ''}:</span>
                <span>₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardContent className="pt-6">
          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Any additional notes or terms"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-1 bg-green-600 hover:bg-green-700"
        >
          {loading ? 'Creating...' : 'Create Quotation'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>

    {/* Add New Service Dialog */}
    <Dialog open={showAddNewServiceDialog} onOpenChange={setShowAddNewServiceDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <AddServiceForm
          formData={{}}
          setFormData={() => {}}
          onServiceAdded={(service) => {
            const price = parseFloat(service.price) || 0;
            const newItem = {
              id: `service_${service.id}_${Date.now()}`,
              type: 'service',
              item_id: service.id,
              name: service.name,
              price: price,
              quantity: 1,
              total: price,
              description: service.description || ''
            };
            setQuotationItems(prev => [...prev, newItem]);
            setShowAddNewServiceDialog(false);
            toast.success('Service added to quotation!');
          }}
        />
      </DialogContent>
    </Dialog>

    {/* Add Existing Service Dialog */}
    <Dialog open={showAddExistingServiceDialog} onOpenChange={setShowAddExistingServiceDialog}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <AddExistingItemsToInvoiceForm
          itemType="service"
          items={services}
          targetLabel="quotation"
          onItemsSelected={(selectedItems) => {
            const newItems = selectedItems.map(it => {
              const qty = it.quantity || 1;
              const price = parseFloat(it.unit_price) || 0;
              return {
                id: `service_${it.item_id}_${Date.now()}_${Math.random()}`,
                type: 'service',
                item_id: it.item_id,
                name: it.name,
                price: price,
                quantity: qty,
                total: qty * price,
                description: it.description || ''
              };
            });
            setQuotationItems(prev => [...prev, ...newItems]);
            setShowAddExistingServiceDialog(false);
            toast.success(`${selectedItems.length} service(s) added to quotation!`);
          }}
          onCancel={() => setShowAddExistingServiceDialog(false)}
        />
      </DialogContent>
    </Dialog>

    {/* Add New Part Dialog */}
    <Dialog open={showAddNewPartDialog} onOpenChange={setShowAddNewPartDialog}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <MasterPartForm
          onSuccess={(part) => {
            const nb = Array.isArray(part.brands) && part.brands.length > 0 ? part.brands[0] : null;
            const sellPrice = nb ? (parseFloat(nb.selling_price) || parseFloat(nb.cost_price) || 0) : (parseFloat(part.selling_price) || parseFloat(part.cost_price) || 0);
            const newItem = {
              id: `part_${part.id}_${Date.now()}`,
              type: 'part',
              item_id: part.id,
              name: part.part_name,
              price: sellPrice,
              quantity: 1,
              total: sellPrice,
              description: part.description || '',
              brand_id: nb ? nb.id : null,
              brand_name: nb ? nb.brand_name : null
            };
            setQuotationItems(prev => [...prev, newItem]);
            setShowAddNewPartDialog(false);
            toast.success('Part added to quotation!');
          }}
          onCancel={() => setShowAddNewPartDialog(false)}
        />
      </DialogContent>
    </Dialog>

    {/* Add Existing Part Dialog */}
    <Dialog open={showAddExistingPartDialog} onOpenChange={setShowAddExistingPartDialog}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
        <AddExistingItemsToInvoiceForm
          itemType="part"
          items={parts}
          targetLabel="quotation"
          onItemsSelected={(selectedItems) => {
            const newItems = selectedItems.map(it => {
              const qty = it.quantity || 1;
              const price = parseFloat(it.unit_price) || 0;
              return {
                id: `part_${it.item_id}_${Date.now()}_${Math.random()}`,
                type: 'part',
                item_id: it.item_id,
                name: it.name,
                price: price,
                quantity: qty,
                total: qty * price,
                description: it.description || '',
                brand_id: it.brand_id || null,
                brand_name: it.brand_name || null
              };
            });
            setQuotationItems(prev => [...prev, ...newItems]);
            setShowAddExistingPartDialog(false);
            toast.success(`${selectedItems.length} part(s) added to quotation!`);
          }}
          onCancel={() => setShowAddExistingPartDialog(false)}
        />
      </DialogContent>
    </Dialog>
    </>
  );
};

// PDF Generation Function
const generateQuotationPDF = (quotation, businessName = 'Garage') => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const currentDate = new Date().toLocaleDateString();
  const validUntil = quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString() : 'Not specified';

  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Quotation - ${quotation.quote_number}</title>
    <style>
        body { 
            font-family: Arial, sans-serif; 
            margin: 20px; 
            color: #333; 
            line-height: 1.6;
        }
        .header { 
            text-align: center; 
            margin-bottom: 30px; 
            border-bottom: 2px solid #2563eb;
            padding-bottom: 20px;
        }
        .logo {
            width: 60px;
            height: 60px;
            margin: 0 auto 15px auto;
            display: block;
        }
        .company-name { 
            font-size: 28px; 
            font-weight: bold; 
            color: #2563eb; 
            margin-bottom: 5px;
        }
        .company-tagline { 
            font-size: 14px; 
            color: #6b7280; 
            margin-bottom: 20px;
        }
        .quotation-title { 
            font-size: 24px; 
            color: #1f2937; 
            margin: 10px 0;
        }
        .quotation-number { 
            font-size: 18px; 
            color: #2563eb; 
            font-weight: bold;
        }
        .info-section { 
            margin: 20px 0; 
            padding: 15px; 
            background-color: #f8fafc; 
            border-left: 4px solid #2563eb;
        }
        .section-title { 
            font-size: 18px; 
            font-weight: bold; 
            color: #1f2937; 
            margin-bottom: 15px;
            border-bottom: 1px solid #e5e7eb;
            padding-bottom: 5px;
        }
        .info-row { 
            display: flex; 
            margin-bottom: 8px; 
        }
        .info-label { 
            font-weight: bold; 
            width: 150px; 
            color: #374151;
        }
        .info-value { 
            flex: 1; 
            color: #1f2937;
        }
        .pricing-table { 
            width: 100%; 
            border-collapse: collapse; 
            margin: 20px 0;
            background-color: white;
            border: 1px solid #e5e7eb;
        }
        .pricing-table th, .pricing-table td { 
            padding: 12px; 
            text-align: left; 
            border-bottom: 1px solid #e5e7eb;
        }
        .pricing-table th { 
            background-color: #f3f4f6; 
            font-weight: bold;
            color: #1f2937;
        }
        .total-row { 
            background-color: #dbeafe; 
            font-weight: bold; 
            color: #2563eb;
        }
        .status-badge {
            display: inline-block;
            padding: 4px 12px;
            border-radius: 20px;
            font-size: 12px;
            font-weight: bold;
        }
        .status-pending { background-color: #fef3c7; color: #92400e; }
        .status-approved { background-color: #dcfce7; color: #166534; }
        .status-rejected { background-color: #fee2e2; color: #991b1b; }
        .footer { 
            margin-top: 30px; 
            padding-top: 20px; 
            border-top: 1px solid #e5e7eb; 
            font-size: 12px; 
            color: #6b7280;
            text-align: center;
        }
        .notes-section {
            margin: 20px 0;
            padding: 15px;
            background-color: #f9fafb;
            border: 1px solid #e5e7eb;
            border-radius: 6px;
        }
    </style>
</head>
<body>
    <div class="header">
        <img src="/logo.png" alt="Garage Logo" class="logo">
        <div class="company-name">${businessName}</div>
        <div class="company-tagline">Professional Automotive Service Center</div>
        <div class="quotation-title">SERVICE QUOTATION</div>
        <div class="quotation-number">${quotation.quote_number}</div>
    </div>

    <div class="info-section">
        <div class="section-title">Quotation Details</div>
        <div class="info-row">
            <div class="info-label">Status:</div>
            <div class="info-value">
                <span class="status-badge status-${quotation.status}">${quotation.status.charAt(0).toUpperCase() + quotation.status.slice(1)}</span>
            </div>
        </div>
        <div class="info-row">
            <div class="info-label">Date Created:</div>
            <div class="info-value">${new Date(quotation.created_at).toLocaleDateString()}</div>
        </div>
        <div class="info-row">
            <div class="info-label">Valid Until:</div>
            <div class="info-value">${validUntil}</div>
        </div>
        <div class="info-row">
            <div class="info-label">Generated On:</div>
            <div class="info-value">${currentDate}</div>
        </div>
    </div>

    <div class="info-section">
        <div class="section-title">Customer Information</div>
        <div class="info-row">
            <div class="info-label">Name:</div>
            <div class="info-value">${quotation.customer_name || 'Not specified'}</div>
        </div>
        <div class="info-row">
            <div class="info-label">Email:</div>
            <div class="info-value">${quotation.customer_email || 'Not specified'}</div>
        </div>
        <div class="info-row">
            <div class="info-label">Phone:</div>
            <div class="info-value">${quotation.customer_phone || quotation.customer_contact || 'Not specified'}</div>
        </div>
        <div class="info-row">
            <div class="info-label">Vehicle:</div>
            <div class="info-value">${quotation.vehicle_info || 'Not specified'}</div>
        </div>
    </div>

    <div class="info-section">
        <div class="section-title">Services & Parts</div>
        ${quotation.items && quotation.items.length > 0 
          ? quotation.items.map(item => `
            <div class="item-row" style="display: flex; justify-content: space-between; padding: 8px; margin: 4px 0; background-color: #f8f9fa; border-radius: 4px;">
              <div>
                <strong>${item.name}</strong> <span style="text-transform: capitalize; color: #666; font-size: 12px;">(${item.type})</span>
                ${item.description ? `<br><span style="color: #666; font-size: 12px;">${item.description}</span>` : ''}
              </div>
              <div style="text-align: right;">
                <div>Qty: ${item.quantity} × ₹${item.price?.toLocaleString('en-IN') || 0}</div>
                <div style="font-weight: bold;">₹${item.total?.toLocaleString('en-IN') || 0}</div>
              </div>
            </div>
          `).join('')
          : '<div class="info-row"><div class="info-value">No services or parts specified</div></div>'
        }
        ${quotation.items && quotation.items.length > 0 
          ? `<div class="subtotal-row" style="margin-top: 12px; padding-top: 8px; border-top: 2px solid #007bff;">
               <div style="display: flex; justify-content: space-between; font-weight: bold; color: #007bff;">
                 <span>Items Subtotal:</span>
                 <span>₹${quotation.items.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString('en-IN')}</span>
               </div>
             </div>`
          : ''
        }
    </div>

    <div class="info-section">
        <div class="section-title">Pricing Breakdown</div>
        <table class="pricing-table">
            <thead>
                <tr>
                    <th>Description</th>
                    <th>Quantity/Hours</th>
                    <th>Rate</th>
                    <th>Amount</th>
                </tr>
            </thead>
            <tbody>
                ${quotation.items && quotation.items.length > 0 
                  ? `<tr>
                       <td>Services & Parts</td>
                       <td>${quotation.items.length} items</td>
                       <td>-</td>
                       <td>${formatCurrency(quotation.items.reduce((sum, item) => sum + (item.total || 0), 0))}</td>
                     </tr>`
                  : ''
                }
                ${quotation.labor_hours && quotation.labor_hours > 0 
                  ? `<tr>
                       <td>Additional Labor</td>
                       <td>${quotation.labor_hours} hours</td>
                       <td>${formatCurrency(quotation.labor_rate || 0)}/hour</td>
                       <td>${formatCurrency((quotation.labor_hours || 0) * (quotation.labor_rate || 0))}</td>
                     </tr>`
                  : ''
                }
                <tr class="total-row">
                    <td colspan="3"><strong>Subtotal (Taxable Value)</strong></td>
                    <td><strong>${formatCurrency(quotation.gst_enabled && quotation.total_tax > 0 ? (quotation.total_taxable_value || 0) : (quotation.total_amount || 0))}</strong></td>
                </tr>
                ${quotation.gst_enabled && quotation.total_tax > 0 ? (
                  quotation.is_interstate
                  ? `<tr><td colspan="3">IGST</td><td>${formatCurrency(quotation.total_igst || 0)}</td></tr>`
                  : `<tr><td colspan="3">CGST</td><td>${formatCurrency(quotation.total_cgst || 0)}</td></tr>
                     <tr><td colspan="3">SGST</td><td>${formatCurrency(quotation.total_sgst || 0)}</td></tr>`
                ) : ''}
                <tr class="total-row">
                    <td colspan="3"><strong>Total Amount</strong></td>
                    <td><strong>${formatCurrency(quotation.total_amount || 0)}</strong></td>
                </tr>
                ${quotation.total_advance_received && quotation.total_advance_received > 0 ? `
                <tr style="background-color: #fef3c7;">
                    <td colspan="3" style="color: #92400e;"><strong>Less: Advance Payment Received</strong></td>
                    <td style="color: #92400e;"><strong>− ${formatCurrency(quotation.total_advance_received)}</strong></td>
                </tr>
                <tr style="background-color: #dcfce7;">
                    <td colspan="3" style="color: #166534;"><strong>Balance Due</strong></td>
                    <td style="color: #166534;"><strong>${formatCurrency((quotation.balance_due !== undefined && quotation.balance_due !== null) ? quotation.balance_due : ((quotation.total_amount || 0) - (quotation.total_advance_received || 0)))}</strong></td>
                </tr>
                ` : ''}
            </tbody>
        </table>
    </div>

    ${quotation.advance_payments && quotation.advance_payments.length > 0 ? `
    <div class="notes-section" style="margin-top: 20px;">
        <div class="section-title">Advance Payment History</div>
        <table class="services-table" style="margin-top: 10px;">
            <thead>
                <tr>
                    <th>Date</th>
                    <th>Payment Method</th>
                    <th>Reference / Notes</th>
                    <th>Amount</th>
                </tr>
            </thead>
            <tbody>
                ${quotation.advance_payments.map(p => `
                <tr>
                    <td>${p.payment_date ? new Date(p.payment_date).toLocaleDateString() : '-'}</td>
                    <td>${(p.payment_method || '').replace(/_/g, ' ').toUpperCase()}</td>
                    <td>${p.reference_number || p.notes || '-'}</td>
                    <td><strong>${formatCurrency(p.amount || 0)}</strong></td>
                </tr>
                `).join('')}
                <tr style="background-color: #fef3c7;">
                    <td colspan="3"><strong>Total Advance Received</strong></td>
                    <td><strong>${formatCurrency(quotation.total_advance_received || 0)}</strong></td>
                </tr>
            </tbody>
        </table>
    </div>
    ` : ''}

    ${quotation.notes ? `
    <div class="notes-section">
        <div class="section-title">Additional Notes</div>
        <p>${quotation.notes}</p>
    </div>
    ` : ''}

    <div class="footer">
        <p><strong>${businessName}</strong> - Professional Automotive Service</p>
        <p>Thank you for choosing our services. This quotation is valid until ${validUntil}.</p>
        <p>For any queries, please contact our customer service team.</p>
    </div>
</body>
</html>
  `;
};

// View Quotation Details Component

const EditQuotationForm = ({ quotation, onQuotationUpdated, onCancel, services = [], parts = [] }) => {
  const [loading, setLoading] = useState(false);

  // Parse legacy vehicle_info string when individual fields are missing.
  // Supports formats like "MERCEDES-BENZ C220 W205 2018" by best-effort splitting.
  const parseLegacyVehicleInfo = (info) => {
    if (!info || typeof info !== 'string') return { make: '', model: '', series: '', year: '' };
    const tokens = info.trim().split(/\s+/);
    let year = '';
    // Year is usually a 4-digit token; extract it
    const yearIdx = tokens.findIndex(t => /^(19|20)\d{2}$/.test(t));
    if (yearIdx >= 0) {
      year = tokens[yearIdx];
      tokens.splice(yearIdx, 1);
    }
    const [make = '', model = '', ...rest] = tokens;
    const series = rest.join(' ');
    return { make, model, series, year };
  };

  const legacy = parseLegacyVehicleInfo(quotation.vehicle_info);
  const initialMake = quotation.vehicle_make || legacy.make || '';
  const initialModel = quotation.vehicle_model || legacy.model || '';
  const initialSeries = quotation.vehicle_series || legacy.series || '';
  const initialYear = quotation.vehicle_year || legacy.year || '';

  // Vehicle Information cascade states
  const [selectedMake, setSelectedMake] = useState(initialMake);
  const [selectedModel, setSelectedModel] = useState(initialModel);
  const [selectedSeries, setSelectedSeries] = useState(initialSeries);
  const [vehicleInfoMakes, setVehicleInfoMakes] = useState([]);
  const [vehicleInfoModels, setVehicleInfoModels] = useState([]);
  const [vehicleInfoSeries, setVehicleInfoSeries] = useState([]);
  
  // Services & Parts management state (same as Invoice)
  const [quotationItems, setQuotationItems] = useState(quotation.items || []);
  const [selectedItemType, setSelectedItemType] = useState("service");
  const [selectedItemId, setSelectedItemId] = useState("");
  const [itemQuantity, setItemQuantity] = useState(1);
  const [gstEnabled, setGstEnabled] = useState(quotation.gst_enabled !== false);
  
  // Vehicle filter states for Services & Parts selection
  const [vehicleFilterMake, setVehicleFilterMake] = useState('all');
  const [vehicleFilterModel, setVehicleFilterModel] = useState('all');
  const [vehicleFilterSeries, setVehicleFilterSeries] = useState('all');
  
  // Vehicle data for filters
  const [vehicleData, setVehicleData] = useState({});
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableModels, setAvailableModels] = useState([]);
  const [availableSeries, setAvailableSeries] = useState([]);

  // Use React Hook Form with existing quotation data
  const { register, handleSubmit, watch, formState: { errors }, reset, setValue } = useForm({
    defaultValues: {
      customer_name: quotation.customer_name || '',
      customer_email: quotation.customer_email || '',
      customer_phone: quotation.customer_phone || quotation.customer_contact || '',
      vehicle_info: quotation.vehicle_info || '',
      vehicle_year: initialYear,
      services: quotation.services || '',
      parts: quotation.parts || '',
      labor_hours: quotation.labor_hours?.toString() || '',
      labor_rate: quotation.labor_rate?.toString() || '800',
      parts_cost: quotation.parts_cost?.toString() || '',
      notes: quotation.notes || '',
      valid_days: '30' // Default for editing
    }
  });

  // Watch values for real-time calculations (updated to use quotationItems)
  const watchedValues = watch(['labor_hours', 'labor_rate']);

  // Fetch vehicle makes for Vehicle Information (from vehicle_data)
  useEffect(() => {
    const fetchInfoMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(m => m && m.trim() !== '');
        setVehicleInfoMakes(makes.sort());
      } catch (error) {
        setVehicleInfoMakes([]);
      }
    };
    fetchInfoMakes();
  }, []);

  useEffect(() => {
    if (!selectedMake || selectedMake === 'Other') { setVehicleInfoModels([]); return; }
    const fetchModels = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(selectedMake)}`);
        setVehicleInfoModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
      } catch (error) { setVehicleInfoModels([]); }
    };
    fetchModels();
  }, [selectedMake]);

  useEffect(() => {
    if (!selectedModel || !selectedMake || selectedModel === 'Other Model') { setVehicleInfoSeries([]); return; }
    const fetchSeries = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(selectedMake)}/${encodeURIComponent(selectedModel)}`);
        setVehicleInfoSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
      } catch (error) { setVehicleInfoSeries([]); }
    };
    fetchSeries();
  }, [selectedModel, selectedMake]);

  // Calculate totals from quotation items
  const calculateTotal = () => {
    const laborCost = (parseFloat(watchedValues[0]) || 0) * (parseFloat(watchedValues[1]) || 0);
    const itemsTotal = quotationItems.reduce((sum, item) => sum + item.total, 0);
    return laborCost + itemsTotal;
  };
  
  // Fetch vehicle data for item filtering (from vehicle_data collection)
  useEffect(() => {
    const fetchVehicleMakes = async () => {
      try {
        const response = await axios.get(`${API}/vehicle-data/makes`);
        const makes = (response.data.makes || []).filter(make => make && make.trim() !== '');
        setAvailableMakes(makes.sort());
      } catch (error) {
        setAvailableMakes([]);
      }
    };
    fetchVehicleMakes();
  }, []);
  
  // Update available models when make filter changes
  useEffect(() => {
    if (vehicleFilterMake === 'all') {
      setAvailableModels([]);
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchModels = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/models/${encodeURIComponent(vehicleFilterMake)}`);
          setAvailableModels((response.data.models || []).filter(m => m && m.trim() !== '').sort());
        } catch (error) {
          setAvailableModels([]);
        }
      };
      fetchModels();
      setVehicleFilterModel('all');
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterMake]);
  
  // Update available series when model filter changes
  useEffect(() => {
    if (vehicleFilterModel === 'all') {
      setAvailableSeries([]);
      setVehicleFilterSeries('all');
    } else {
      const fetchSeries = async () => {
        try {
          const response = await axios.get(`${API}/vehicle-data/series/${encodeURIComponent(vehicleFilterMake)}/${encodeURIComponent(vehicleFilterModel)}`);
          setAvailableSeries((response.data.series || []).filter(s => s && s.trim() !== '').sort());
        } catch (error) {
          setAvailableSeries([]);
        }
      };
      fetchSeries();
      setVehicleFilterSeries('all');
    }
  }, [vehicleFilterModel, vehicleFilterMake]);

  // Get available items based on selected type (same as Invoice)
  const getAvailableItems = () => {
    let items = [];
    
    if (selectedItemType === "service") {
      items = services || [];
    } else {
      items = parts || [];
    }
    
    // Apply vehicle compatibility filters if any are selected
    if (vehicleFilterMake !== 'all' || vehicleFilterModel !== 'all' || vehicleFilterSeries !== 'all') {
      items = items.filter(item => {
        // If item has no compatibility data, show it in all filters (backward compatibility)
        if (!item.compatible_makes || item.compatible_makes.length === 0) {
          return true;
        }
        
        // Check make compatibility
        const makeMatches = vehicleFilterMake === 'all' || 
          item.compatible_makes.includes(vehicleFilterMake);
        
        // Check model compatibility  
        const modelMatches = vehicleFilterModel === 'all' || 
          !item.compatible_models || 
          item.compatible_models.length === 0 || 
          item.compatible_models.includes(vehicleFilterModel);
        
        // Check series compatibility
        const seriesMatches = vehicleFilterSeries === 'all' || 
          !item.compatible_series || 
          item.compatible_series.length === 0 || 
          item.compatible_series.includes(vehicleFilterSeries);
        
        return makeMatches && modelMatches && seriesMatches;
      });
    }
    
    return items;
  };

  // Add item to quotation (same as Invoice)
  const addQuotationItem = () => {
    if (!selectedItemId) {
      alert("Please select an item");
      return;
    }

    const availableItems = getAvailableItems();
    const selectedItem = availableItems.find(item => item.id === selectedItemId);
    
    if (!selectedItem) {
      alert("Selected item not found");
      return;
    }

    const isPart = selectedItemType === 'part';
    const pb = isPart && Array.isArray(selectedItem.brands) && selectedItem.brands.length > 0 ? selectedItem.brands[0] : null;
    const price = pb ? (parseFloat(pb.selling_price) || parseFloat(pb.cost_price) || 0) : (selectedItem.price || selectedItem.selling_price || selectedItem.cost_price || 0);
    const total = price * itemQuantity;

    const newItem = {
      id: `${selectedItemType}_${selectedItemId}_${Date.now()}`,
      type: selectedItemType,
      item_id: selectedItemId,
      name: selectedItem.name || selectedItem.part_name || selectedItem.description,
      price: price,
      quantity: itemQuantity,
      total: total,
      description: selectedItem.description || '',
      brand_id: pb ? pb.id : null,
      brand_name: pb ? pb.brand_name : null
    };

    setQuotationItems(prev => [...prev, newItem]);
    setSelectedItemId("");
    setItemQuantity(1);
  };

  // Remove item from quotation
  const removeQuotationItem = (itemId) => {
    setQuotationItems(prev => prev.filter(item => item.id !== itemId));
  };

  // Update item in quotation
  const updateQuotationItem = (itemId, field, value) => {
    setQuotationItems(prev => prev.map(item => {
      if (item.id === itemId) {
        const updatedItem = { ...item, [field]: value };
        if (field === 'quantity' || field === 'price') {
          updatedItem.total = updatedItem.quantity * updatedItem.price;
        }
        return updatedItem;
      }
      return item;
    }));
  };

  const onSubmit = (data) => {
    setLoading(true);

    try {
      if (!data.customer_name.trim()) {
        alert('Please enter customer name');
        return;
      }
      if (!selectedMake.trim() || !selectedModel.trim()) {
        alert('Please select vehicle make and model');
        return;
      }

      // Rebuild vehicle_info from cascade values
      const vehicleInfo = [
        selectedMake.trim(),
        selectedModel.trim(),
        selectedSeries.trim(),
        (data.vehicle_year || '').trim()
      ].filter(Boolean).join(' ');

      const phoneValue = (data.customer_phone || '').trim();
      const updatedQuotationData = {
        ...quotation, // Keep existing fields like id, quote_number, created_at
        customer_name: data.customer_name.trim(),
        customer_email: data.customer_email.trim(),
        customer_phone: phoneValue,
        customer_contact: phoneValue, // Backend uses customer_contact field
        vehicle_info: vehicleInfo,
        vehicle_make: selectedMake.trim(),
        vehicle_model: selectedModel.trim(),
        vehicle_series: selectedSeries.trim() || null,
        vehicle_year: (data.vehicle_year || '').trim() || null,
        items: quotationItems, // Using quotationItems instead of services/parts text
        labor_hours: parseFloat(data.labor_hours) || 0,
        labor_rate: parseFloat(data.labor_rate) || 0,
        total_amount: calculateTotal(),
        notes: data.notes.trim(),
        gst_enabled: gstEnabled,
        valid_until: new Date(Date.now() + (parseInt(data.valid_days) * 24 * 60 * 60 * 1000)).toISOString().split('T')[0]
      };

      onQuotationUpdated(updatedQuotationData);
    } catch (error) {
      console.error('Error updating quotation:', error);
      alert('An error occurred while updating the quotation.');
    } finally {
      setLoading(false);
    }
  };

  // Use the same form structure as AddQuotationForm but with "Update" instead of "Create"
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_name">Customer Name *</Label>
              <Input
                id="customer_name"
                {...register("customer_name", { 
                  required: "Customer name is required",
                  minLength: { value: 2, message: "Name must be at least 2 characters" }
                })}
                placeholder="Enter customer name"
              />
              {errors.customer_name && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="customer_email">Email</Label>
              <Input
                id="customer_email"
                type="email"
                {...register("customer_email", {
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address"
                  }
                })}
                placeholder="customer@example.com"
              />
              {errors.customer_email && (
                <p className="text-sm text-red-600 mt-1">{errors.customer_email.message}</p>
              )}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="customer_phone">Phone Number</Label>
              <Input
                id="customer_phone"
                {...register("customer_phone")}
                placeholder="+91-9876543210"
              />
            </div>
          </div>

          {/* Vehicle Information - Cascading Dropdowns */}
          <div>
            <Label className="text-base font-semibold">🚗 Vehicle Information</Label>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-2">
              {/* MAKE Dropdown */}
              <div>
                <Label htmlFor="vehicle_make">MAKE *</Label>
                <Select
                  value={selectedMake}
                  onValueChange={(value) => {
                    setSelectedMake(value);
                    setValue('vehicle_make', value);
                    // Reset model and series when make changes
                    setSelectedModel('');
                    setSelectedSeries('');
                    setValue('vehicle_model', '');
                    setValue('vehicle_series', '');
                  }}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue placeholder="Select vehicle MAKE" />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {vehicleInfoMakes.map((make) => (
                      <SelectItem key={make} value={make}>{make}</SelectItem>
                    ))}
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
                {!selectedMake && (
                  <p className="text-sm text-red-600 mt-1">Vehicle make is required</p>
                )}
              </div>

              {/* MODEL Dropdown */}
              <div>
                <Label htmlFor="vehicle_model">MODEL *</Label>
                <Select
                  value={selectedModel}
                  onValueChange={(value) => {
                    setSelectedModel(value);
                    setValue('vehicle_model', value);
                    // Reset series when model changes
                    setSelectedSeries('');
                    setValue('vehicle_series', '');
                  }}
                  disabled={!selectedMake}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue 
                      placeholder={
                        selectedMake 
                          ? `Select ${selectedMake} MODEL` 
                          : "Select MAKE first"
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {vehicleInfoModels.length > 0 ? (
                      vehicleInfoModels.map((model) => (
                        <SelectItem key={model} value={model}>
                          {model}
                        </SelectItem>
                      ))
                    ) : selectedMake ? (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        No predefined models for {selectedMake}
                      </div>
                    ) : null}
                    {selectedMake && selectedMake !== 'Other' && (
                      <SelectItem value="Other Model">Other {selectedMake} Model</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedMake && (
                  <div className="text-xs text-gray-500 mt-1">
                    {vehicleInfoModels.length > 0 
                      ? `${vehicleInfoModels.length} ${selectedMake} MODELs available`
                      : `Select ${selectedMake} MODEL`
                    }
                  </div>
                )}
                {!selectedModel && selectedMake && (
                  <p className="text-sm text-red-600 mt-1">Vehicle model is required</p>
                )}
              </div>

              {/* SERIES Dropdown */}
              <div>
                <Label htmlFor="vehicle_series">SERIES</Label>
                <Select
                  value={selectedSeries}
                  onValueChange={(value) => {
                    setSelectedSeries(value);
                    setValue('vehicle_series', value);
                  }}
                  disabled={!selectedMake || !selectedModel}
                >
                  <SelectTrigger className="bg-white">
                    <SelectValue 
                      placeholder={
                        !selectedMake ? "Select MAKE first" :
                        !selectedModel ? "Select MODEL first" :
                        `Select ${selectedModel} SERIES`
                      } 
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {vehicleInfoSeries.length > 0 ? (
                      vehicleInfoSeries.map((series) => (
                        <SelectItem key={series} value={series}>
                          {series}
                        </SelectItem>
                      ))
                    ) : selectedMake && selectedModel ? (
                      <div className="p-2 text-sm text-gray-500 text-center">
                        No predefined series for {selectedModel}
                      </div>
                    ) : null}
                    {selectedMake && selectedModel && selectedModel !== 'Other Model' && (
                      <SelectItem value="Other Series">Other {selectedModel} Series</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                {selectedMake && selectedModel && (
                  <div className="text-xs text-gray-500 mt-1">
                    {vehicleInfoSeries.length > 0 
                      ? `${vehicleInfoSeries.length} ${selectedModel} SERIES available`
                      : `Select ${selectedModel} SERIES`
                    }
                  </div>
                )}
                {!selectedMake || !selectedModel ? (
                  <p className="text-xs text-gray-500 mt-1">Optional: Select MAKE and MODEL first to see SERIES options</p>
                ) : null}
              </div>

              {/* Year Field (unchanged) */}
              <div>
                <Label htmlFor="vehicle_year">Year</Label>
                <Input
                  id="vehicle_year"
                  type="number"
                  min="1980"
                  max="2030"
                  {...register("vehicle_year")}
                  placeholder="e.g., 2020"
                />
                <p className="text-xs text-gray-500 mt-1">Optional: Model year</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services & Parts (same as Invoice) */}
      <Card>
        <CardHeader>
          <CardTitle>Services & Parts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Vehicle Filters */}
          <div className="mb-4 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 mb-3">
              <Filter className="h-4 w-4 text-gray-600" />
              <Label className="text-sm font-medium text-gray-700">Filter by Vehicle Compatibility (Optional)</Label>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label htmlFor="vehicle_filter_make">Make</Label>
                <Select value={vehicleFilterMake} onValueChange={setVehicleFilterMake}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All Makes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Makes</SelectItem>
                    {availableMakes.map(make => (
                      <SelectItem key={make} value={make}>{make}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="vehicle_filter_model">Model</Label>
                <Select value={vehicleFilterModel} onValueChange={setVehicleFilterModel} disabled={vehicleFilterMake === 'all'}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All Models" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Models</SelectItem>
                    {availableModels.map(model => (
                      <SelectItem key={model} value={model}>{model}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="vehicle_filter_series">Series</Label>
                <Select value={vehicleFilterSeries} onValueChange={setVehicleFilterSeries} disabled={vehicleFilterModel === 'all'}>
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All Series" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Series</SelectItem>
                    {availableSeries.map(series => (
                      <SelectItem key={series} value={series}>{series}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            {(vehicleFilterMake !== 'all' || vehicleFilterModel !== 'all' || vehicleFilterSeries !== 'all') && (
              <div className="flex items-center gap-2 mt-3 text-sm text-blue-600">
                <Info className="h-4 w-4" />
                <span>
                  Showing {selectedItemType}s compatible with: {vehicleFilterMake !== 'all' ? vehicleFilterMake : 'All Makes'}
                  {vehicleFilterModel !== 'all' ? ` → ${vehicleFilterModel}` : ''}
                  {vehicleFilterSeries !== 'all' ? ` → ${vehicleFilterSeries}` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Add Items Section */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label htmlFor="item_type">Item Type</Label>
              <Select value={selectedItemType} onValueChange={setSelectedItemType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="service">Service</SelectItem>
                  <SelectItem value="part">Part</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="item_select">Select {selectedItemType === "service" ? "Service" : "Part"} ({getAvailableItems().length} available)</Label>
              <Select value={selectedItemId} onValueChange={setSelectedItemId}>
                <SelectTrigger>
                  <SelectValue placeholder={`Choose ${selectedItemType}`} />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableItems().map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name || item.part_name || item.description} - ₹{item.price || item.selling_price || item.cost_price || 0}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="quantity">Quantity</Label>
              <Input
                type="number"
                min="0.01"
                step="0.01"
                value={itemQuantity}
                onChange={(e) => setItemQuantity(parseFloat(e.target.value) || 1)}
                placeholder="1"
              />
            </div>

            <div className="flex items-end">
              <Button 
                type="button" 
                onClick={addQuotationItem} 
                className="w-full bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Item
              </Button>
            </div>
          </div>

          {/* Added Items Display */}
          <div className="space-y-2">
            {quotationItems.length === 0 ? (
              <div className="text-center py-8 text-gray-500 border-2 border-dashed border-gray-200 rounded-lg">
                <Package className="h-8 w-8 mx-auto mb-2" />
                <p>No items added yet</p>
                <p className="text-sm">Add services or parts to create the quotation</p>
              </div>
            ) : (
              quotationItems.map(item => (
                <QuotationItemRow 
                  key={item.id} 
                  item={item} 
                  onUpdate={updateQuotationItem} 
                  onRemove={() => removeQuotationItem(item.id)} 
                />
              ))
            )}
          </div>
        </CardContent>
      </Card>

      {/* Pricing */}
      <Card>
        <CardHeader>
          <CardTitle>Additional Pricing (Indian Rupees)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label htmlFor="labor_hours">Additional Labor Hours</Label>
              <Input
                id="labor_hours"
                type="number"
                step="0.5"
                min="0"
                {...register("labor_hours")}
                placeholder="0"
              />
              <p className="text-xs text-gray-500 mt-1">Extra labor beyond service items</p>
            </div>
            <div>
              <Label htmlFor="labor_rate">Labor Rate (₹/hour)</Label>
              <Input
                id="labor_rate"
                type="number"
                min="0"
                {...register("labor_rate")}
                placeholder="800"
              />
            </div>
            <div>
              <Label htmlFor="valid_days">Valid for (days)</Label>
              <Input
                id="valid_days"
                type="number"
                min="1"
                {...register("valid_days")}
                placeholder="30"
              />
            </div>
          </div>

          {/* Total Calculation */}
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center justify-between mb-3 pb-3 border-b border-blue-200">
              <div>
                <Label htmlFor="quotation-gst-toggle" className="font-medium">Apply GST</Label>
                <p className="text-xs text-muted-foreground">CGST+SGST or IGST added automatically to the total.</p>
              </div>
              <Switch id="quotation-gst-toggle" data-testid="quotation-gst-toggle" checked={gstEnabled} onCheckedChange={setGstEnabled} />
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>Items Total:</span>
                <span>₹{quotationItems.reduce((sum, item) => sum + item.total, 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span>Additional Labor Cost:</span>
                <span>₹{((parseFloat(watchedValues[0]) || 0) * (parseFloat(watchedValues[1]) || 0)).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
              {gstEnabled && (
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>GST:</span>
                  <span>Calculated on save (CGST+SGST or IGST)</span>
                </div>
              )}
              <hr className="border-blue-200" />
              <div className="flex justify-between text-lg font-bold text-blue-700">
                <span>Total Amount{gstEnabled ? ' (excl. GST, shown after saving)' : ''}:</span>
                <span>₹{calculateTotal().toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Notes */}
      <Card>
        <CardContent className="pt-6">
          <div>
            <Label htmlFor="notes">Additional Notes</Label>
            <Textarea
              id="notes"
              {...register("notes")}
              placeholder="Any additional notes or terms"
              rows={3}
            />
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center gap-4">
        <Button 
          type="submit" 
          disabled={loading}
          className="flex-1 bg-blue-600 hover:bg-blue-700"
        >
          {loading ? 'Updating...' : 'Update Quotation'}
        </Button>
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          disabled={loading}
          className="flex-1"
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Add Appointment Form Component

const ViewQuotationDetails = ({ 
  quotation, 
  onClose, 
  onRecordAdvance, 
  onGenerateReceipt 
}) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'approved':
        return 'bg-green-100 text-green-800';
      case 'rejected':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const generateAdvanceReceipt = (payment, quotation) => {
    // Generate advance payment receipt
    console.log('Generating advance receipt for payment:', payment);
    toast.success('Receipt generated successfully!');
  };

  return (
    <div className="space-y-6">
      {/* Header Info */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Quotation Number</p>
            <p className="text-lg font-semibold text-blue-700">{quotation.quote_number}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Status</p>
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(quotation.status)}`}>
              {quotation.status.charAt(0).toUpperCase() + quotation.status.slice(1)}
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4 mt-4">
          <div>
            <p className="text-sm font-medium text-gray-600">Created Date</p>
            <p className="text-sm text-gray-800">{new Date(quotation.created_at).toLocaleDateString()}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Valid Until</p>
            <p className="text-sm text-gray-800">{quotation.valid_until ? new Date(quotation.valid_until).toLocaleDateString() : 'Not specified'}</p>
          </div>
        </div>
      </div>

      {/* Customer Information */}
      <Card>
        <CardHeader>
          <CardTitle>Customer Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Name</p>
              <p className="text-sm text-gray-800">{quotation.customer_name || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Email</p>
              <p className="text-sm text-gray-800">{quotation.customer_email || 'Not specified'}</p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-600">Phone</p>
              <p className="text-sm text-gray-800">{quotation.customer_phone || quotation.customer_contact || 'Not specified'}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-600">Vehicle</p>
              <p className="text-sm text-gray-800">{quotation.vehicle_info || 'Not specified'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Services & Parts Details */}
      <Card>
        <CardHeader>
          <CardTitle>Services & Parts</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {quotation.items && quotation.items.length > 0 ? (
            <div className="space-y-3">
              {quotation.items.map((item, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-sm">{item.name}</p>
                        <p className="text-xs text-gray-500 capitalize">{item.type}</p>
                        {item.description && (
                          <p className="text-xs text-gray-600 mt-1">{item.description}</p>
                        )}
                      </div>
                      <div className="flex items-center space-x-4 text-sm">
                        <div className="text-right">
                          <p className="text-gray-500">Qty: {item.quantity}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-gray-500">Price: ₹{item.price?.toLocaleString('en-IN')}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-semibold">Total: ₹{item.total?.toLocaleString('en-IN')}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              <div className="mt-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex justify-between font-semibold text-blue-700">
                  <span>Items Subtotal:</span>
                  <span>₹{quotation.items.reduce((sum, item) => sum + (item.total || 0), 0).toLocaleString('en-IN')}</span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <p>No services or parts specified</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Pricing Breakdown */}
      <Card>
        <CardHeader>
          <CardTitle>Pricing Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            {quotation.items && quotation.items.length > 0 && (
              <div className="flex justify-between text-sm">
                <span>Services & Parts Total:</span>
                <span>{formatCurrency(quotation.items.reduce((sum, item) => sum + (item.total || 0), 0))}</span>
              </div>
            )}
            {quotation.labor_hours && quotation.labor_hours > 0 && (
              <div className="flex justify-between text-sm">
                <span>Additional Labor ({quotation.labor_hours} hours × {formatCurrency(quotation.labor_rate || 0)}/hour):</span>
                <span>{formatCurrency((quotation.labor_hours || 0) * (quotation.labor_rate || 0))}</span>
              </div>
            )}
            {quotation.gst_enabled && quotation.total_tax > 0 && (
              quotation.is_interstate ? (
                <div className="flex justify-between text-sm" data-testid="quotation-igst-row">
                  <span>IGST:</span>
                  <span>{formatCurrency(quotation.total_igst || 0)}</span>
                </div>
              ) : (
                <>
                  <div className="flex justify-between text-sm" data-testid="quotation-cgst-row">
                    <span>CGST:</span>
                    <span>{formatCurrency(quotation.total_cgst || 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm" data-testid="quotation-sgst-row">
                    <span>SGST:</span>
                    <span>{formatCurrency(quotation.total_sgst || 0)}</span>
                  </div>
                </>
              )
            )}
            <hr className="border-gray-300" />
            <div className="flex justify-between text-lg font-bold text-blue-700">
              <span>Total Amount:</span>
              <span>{formatCurrency(quotation.total_amount || 0)}</span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Additional Notes */}
      {quotation.notes && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{quotation.notes}</p>
          </CardContent>
        </Card>
      )}

      {/* Advance Payment Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Payment Information</span>
            <Button 
              size="sm" 
              className="bg-green-600 hover:bg-green-700"
              onClick={() => onRecordAdvance(quotation)}
            >
              <Plus className="h-4 w-4 mr-1" />
              Record Advance
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Payment Summary */}
            <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Total Amount</p>
                <p className="text-lg font-bold text-blue-600">{formatCurrency(quotation.total_amount || 0)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Advance Received</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(quotation.total_advance_received || 0)}</p>
              </div>
              <div className="text-center">
                <p className="text-sm font-medium text-gray-600">Balance Due</p>
                <p className="text-lg font-bold text-orange-600">{formatCurrency((quotation.total_amount || 0) - (quotation.total_advance_received || 0))}</p>
              </div>
            </div>

            {/* Advance Payment History */}
            {quotation.advance_payments && quotation.advance_payments.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-800 mb-3">Advance Payment History</h4>
                <div className="space-y-2">
                  {quotation.advance_payments.map((payment, index) => (
                    <div key={payment.id || index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                      <div>
                        <p className="font-medium text-green-600">₹{payment.amount?.toLocaleString('en-IN')}</p>
                        <p className="text-sm text-gray-500">
                          {payment.payment_method?.replace('_', ' ').toUpperCase()} • {new Date(payment.payment_date).toLocaleDateString('en-IN')}
                        </p>
                        {payment.reference_number && (
                          <p className="text-xs text-gray-400">Ref: {payment.reference_number}</p>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => onGenerateReceipt(payment, quotation)}
                      >
                        <FileText className="h-4 w-4 mr-1" />
                        Receipt
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex justify-end">
        <Button onClick={onClose} variant="outline">
          Close
        </Button>
      </div>
    </div>
  );
};

// Edit Quotation Form Component

const QuotationsSection = ({ onUpdate }) => {
  const businessName = useGarageBranding();
  const [quotations, setQuotations] = useState([]);
  const [services, setServices] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showViewDialog, setShowViewDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  
  // Advance Payment States
  const [showAdvancePaymentDialog, setShowAdvancePaymentDialog] = useState(false);
  const [advancePaymentData, setAdvancePaymentData] = useState({
    amount: '',
    payment_method: 'cash',
    bank_account_id: '',
    reference_number: '',
    notes: '',
    percentage: '',
    is_percentage: false
  });
  const [bankAccounts, setBankAccounts] = useState([]);

  // Fetch services from backend
  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services`);
      setServices(response.data || []);
    } catch (error) {
      console.error("Error fetching services:", error);
      setServices([]);
    }
  };

  // Fetch parts from backend
  const fetchParts = async () => {
    try {
      const response = await axios.get(`${API}/parts`);
      setParts(response.data || []);
    } catch (error) {
      console.error("Error fetching parts:", error);
      setParts([]);
    }
  };

  // Load quotations data from backend API
  const loadQuotations = async () => {
    setLoading(true);
    try {
      // Fetch quotations from backend API
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/quotations`);
      console.log('Quotations loaded from backend:', response.data);
      setQuotations(response.data || []);
    } catch (error) {
      console.error('Error loading quotations from backend:', error);
      
      // Show empty state if backend fails
      console.log('Backend connection failed, showing empty state');
      setQuotations([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadQuotations();
    fetchServices();
    fetchParts();
    fetchBankAccounts();
  }, []);

  const fetchBankAccounts = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/bank-accounts`);
      setBankAccounts(response.data);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
      setBankAccounts([]);
    }
  };

  const fetchInvoicePaymentSummary = async (invoiceId) => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/invoices/${invoiceId}/payment-summary`);
      return response.data;
    } catch (error) {
      console.error("Error fetching invoice payment summary:", error);
      return null;
    }
  };

  // Handle adding new quotation
  const handleQuotationAdded = async (newQuotation) => {
    try {
      // Save to backend API
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotations`, 
        newQuotation
      );
      
      if (response.data) {
        // Add the new quotation to local state
        setQuotations([...quotations, response.data]);
        setShowAddDialog(false);
        if (onUpdate) onUpdate();
        
        // Show success message
        toast.success(`Quotation ${response.data.quote_number} created successfully!`, {
          description: `Customer: ${response.data.customer_name} | Amount: ₹${response.data.total_amount?.toLocaleString()}`
        });
      }
    } catch (error) {
      console.error('Error saving quotation to backend:', error);
      alert('Failed to save quotation. Please try again.');
    }
  };

  // Handle viewing quotation
  const handleViewQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowViewDialog(true);
  };

  // Handle editing quotation
  const handleEditQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowEditDialog(true);
  };

  // Handle quotation updated
  const handleQuotationUpdated = async (updatedQuotation) => {
    try {
      // Persist changes to the backend
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${updatedQuotation.id}`,
        updatedQuotation
      );
      const saved = response.data;
      setQuotations(quotations.map(q => 
        q.id === saved.id ? saved : q
      ));
      toast.success('Quotation updated successfully');
      setShowEditDialog(false);
      setSelectedQuotation(null);
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error('Error updating quotation:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : (error.message || 'Failed to update quotation'));
      toast.error(`Update failed: ${msg}`);
    }
  };

  // Handle status change
  const handleStatusChange = async (quotationId, newStatus) => {
    try {
      // Update status in backend API
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${quotationId}`,
        { status: newStatus }
      );
      
      // Update local state with response data
      const updatedQuotation = response.data;
      setQuotations(quotations.map(q => 
        q.id === quotationId ? updatedQuotation : q
      ));
      
      const statusLabel = newStatus.charAt(0).toUpperCase() + newStatus.slice(1);
      toast.success(`Quotation marked as ${statusLabel}`);
      console.log('Quotation status updated successfully:', updatedQuotation);
    } catch (error) {
      console.error('Error updating quotation status:', error);
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail)
        ? detail.map(d => `${(d.loc || []).join('.')}: ${d.msg}`).join(', ')
        : (typeof detail === 'string' ? detail : (error.message || 'Failed to update status'));
      toast.error(`Update failed: ${msg}`);
    }
    
    if (onUpdate) onUpdate();
  };

  // Convert a quotation into an invoice (applies remaining advances once)
  const handleConvertToInvoice = async (quotation, allowMultiple = false) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/invoices/from-quotation/${quotation.id}`,
        { allow_multiple: allowMultiple }
      );
      const invoice = response.data;
      toast.success(`Invoice ${invoice.invoice_number} created from ${quotation.quote_number}`, {
        description: (invoice.total_advance_received > 0)
          ? `Advance applied: ₹${Number(invoice.total_advance_received).toLocaleString('en-IN')} · Balance due: ₹${Number(invoice.balance_due).toLocaleString('en-IN')}`
          : `Balance due: ₹${Number(invoice.balance_due).toLocaleString('en-IN')}`
      });
      await loadQuotations();
      if (onUpdate) onUpdate();
    } catch (error) {
      const status = error.response?.status;
      const detail = error.response?.data?.detail;
      if (status === 409 && !allowMultiple) {
        const proceed = window.confirm(
          `${typeof detail === 'string' ? detail : 'This quotation is already converted.'}\n\nDo you want to create ANOTHER invoice from this quotation anyway?`
        );
        if (proceed) return handleConvertToInvoice(quotation, true);
        return;
      }
      console.error('Error converting quotation to invoice:', error);
      toast.error(typeof detail === 'string' ? detail : 'Failed to convert quotation to invoice');
    }
  };


  const handleDeleteQuotation = async (quotation) => {
    if (!window.confirm(`Are you sure you want to delete quotation ${quotation.quote_number}?`)) return;
    
    try {
      await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/quotations/${quotation.id}`);
      setQuotations(quotations.filter(q => q.id !== quotation.id));
      toast.success("Quotation deleted successfully!");
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Error deleting quotation:", error);
      toast.error("Failed to delete quotation");
    }
  };

  // Handle PDF generation — opens the generated HTML in a new window and triggers browser print
  // The user can then choose "Save as PDF" from the print dialog destination.
  const handleGeneratePDF = (quotation) => {
    try {
      const pdfContent = generateQuotationPDF(quotation, businessName);

      // Open in a new window and auto-trigger print dialog
      const printWindow = window.open('', '_blank', 'width=900,height=1200');
      if (!printWindow) {
        toast.error('Please allow pop-ups for this site to download the PDF');
        return;
      }

      // Set a sensible filename for the resulting PDF (browser uses document.title)
      const html = pdfContent.replace(
        /<title>[^<]*<\/title>/i,
        `<title>Quotation-${quotation.quote_number}</title>`
      );

      printWindow.document.open();
      printWindow.document.write(html);
      printWindow.document.close();

      // Wait for content (images, fonts) to load before opening print dialog
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 300);
      };

      toast.success('Print dialog opened — choose "Save as PDF" as destination');
      console.log('PDF print initiated for quotation:', quotation.quote_number);
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('An error occurred while generating the PDF. Please try again.');
    }
  };

  // Advance Payment Functions
  const handleAdvancePayment = async () => {
    try {
      if (!selectedQuotation) {
        toast.error('No quotation selected');
        return;
      }

      let finalAmount;
      if (advancePaymentData.is_percentage) {
        if (!advancePaymentData.percentage || parseFloat(advancePaymentData.percentage) <= 0 || parseFloat(advancePaymentData.percentage) > 100) {
          toast.error('Please enter valid percentage (1-100%)');
          return;
        }
        finalAmount = (selectedQuotation.total_amount * parseFloat(advancePaymentData.percentage)) / 100;
      } else {
        if (!advancePaymentData.amount || parseFloat(advancePaymentData.amount) <= 0) {
          toast.error('Please enter valid amount');
          return;
        }
        finalAmount = parseFloat(advancePaymentData.amount);
      }

      // Check if advance doesn't exceed remaining balance
      const currentBalance = selectedQuotation.total_amount - (selectedQuotation.total_advance_received || 0);
      if (finalAmount > currentBalance) {
        toast.error(`Advance amount cannot exceed remaining balance of ₹${currentBalance.toLocaleString('en-IN')}`);
        return;
      }

      // Validate bank account selection for bank transfers
      if (advancePaymentData.payment_method === 'bank_transfer' && !advancePaymentData.bank_account_id) {
        toast.error('Please select a bank account for bank transfer');
        return;
      }

      const paymentData = {
        amount: finalAmount,
        payment_method: advancePaymentData.payment_method,
        bank_account_id: advancePaymentData.bank_account_id || null,
        reference_number: advancePaymentData.reference_number || null,
        notes: advancePaymentData.notes || null
      };

      // Call backend API
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/quotations/${selectedQuotation.id}/advance-payment`,
        paymentData
      );

      // Update local quotation data
      const updatedQuotation = {
        ...selectedQuotation,
        advance_payments: [...(selectedQuotation.advance_payments || []), response.data.advance_payment],
        total_advance_received: response.data.total_advance_received,
        balance_due: response.data.balance_due,
        status: 'advance_received'
      };

      setSelectedQuotation(updatedQuotation);
      
      // Update quotations list
      setQuotations(prev => prev.map(q => q.id === selectedQuotation.id ? updatedQuotation : q));

      toast.success('Advance payment recorded successfully');
      setShowAdvancePaymentDialog(false);
      setAdvancePaymentData({
        amount: '',
        payment_method: 'cash',
        bank_account_id: '',
        reference_number: '',
        notes: '',
        percentage: '',
        is_percentage: false
      });
    } catch (error) {
      console.error('Error recording advance payment:', error);
      toast.error('Failed to record advance payment');
    }
  };

  const generateAdvanceReceipt = (payment, quotation) => {
    const receiptContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Advance Payment Receipt - ${quotation.quote_number}</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #333; }
          .header { text-align: center; margin-bottom: 20px; border-bottom: 2px solid #2563eb; padding-bottom: 15px; }
          .company-name { font-size: 24px; font-weight: bold; color: #2563eb; }
          .receipt-title { font-size: 18px; color: #1f2937; margin: 10px 0; }
          .info-section { margin: 15px 0; padding: 10px; background-color: #f8fafc; border-left: 4px solid #2563eb; }
          .info-row { display: flex; justify-content: space-between; margin: 5px 0; }
          .info-label { font-weight: bold; }
          .amount-section { background-color: #f0f9ff; padding: 15px; border: 2px solid #2563eb; border-radius: 8px; text-align: center; margin: 20px 0; }
          .amount { font-size: 24px; font-weight: bold; color: #2563eb; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="company-name">${businessName.toUpperCase()}</div>
          <div class="receipt-title">ADVANCE PAYMENT RECEIPT</div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Receipt Date:</span>
            <span>${new Date().toLocaleDateString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Payment Date:</span>
            <span>${new Date(payment.payment_date).toLocaleDateString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Quotation Number:</span>
            <span>${quotation.quote_number}</span>
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Customer:</span>
            <span>${quotation.customer_name}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Vehicle:</span>
            <span>${quotation.vehicle_info}</span>
          </div>
        </div>

        <div class="amount-section">
          <div>ADVANCE PAYMENT RECEIVED</div>
          <div class="amount">₹${payment.amount.toLocaleString('en-IN')}</div>
          <div style="margin-top: 10px;">
            <strong>Payment Method:</strong> ${payment.payment_method.replace('_', ' ').toUpperCase()}
            ${payment.reference_number ? `<br><strong>Reference:</strong> ${payment.reference_number}` : ''}
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="info-label">Original Quote Amount:</span>
            <span>₹${quotation.total_amount.toLocaleString('en-IN')}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Advance Received:</span>
            <span>₹${quotation.total_advance_received.toLocaleString('en-IN')}</span>
          </div>
          <div class="info-row" style="border-top: 1px solid #ccc; padding-top: 5px; margin-top: 5px;">
            <span class="info-label"><strong>Remaining Balance:</strong></span>
            <span><strong>₹${(quotation.total_amount - quotation.total_advance_received).toLocaleString('en-IN')}</strong></span>
          </div>
        </div>

        ${payment.notes ? `
        <div class="info-section">
          <div class="info-label">Notes:</div>
          <div style="margin-top: 5px;">${payment.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <p><strong>Thank you for your advance payment!</strong></p>
          <p>This receipt is valid proof of advance payment received by the Garage.</p>
          <p>Balance amount will be collected upon completion of services.</p>
        </div>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(receiptContent);
    printWindow.document.close();
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 250);
    };
  };

  const filteredQuotations = quotations.filter(quote => {
    const matchesSearch = searchTerm === '' || 
      (quote.customer_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quote.quote_number || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (quote.vehicle_info || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || quote.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  }).sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quotations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
          <p className="text-gray-600">Manage customer quotations and estimates</p>
        </div>
        <Button 
          className="bg-green-600 hover:bg-green-700"
          onClick={() => setShowAddDialog(true)}
        >
          <Plus className="h-4 w-4 mr-2" />
          New Quotation
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <FileText className="h-8 w-8 text-blue-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Quotations</p>
                <p className="text-2xl font-bold text-gray-900">{quotations.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <Clock className="h-8 w-8 text-orange-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Pending</p>
                <p className="text-2xl font-bold text-gray-900">
                  {quotations.filter(q => q.status === 'pending').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <CheckCircle className="h-8 w-8 text-green-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Approved</p>
                <p className="text-2xl font-bold text-gray-900">
                  {quotations.filter(q => q.status === 'approved').length}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center">
              <DollarSign className="h-8 w-8 text-purple-600" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Value</p>
                <p className="text-2xl font-bold text-gray-900">
                  ₹{quotations.reduce((sum, q) => sum + q.total_amount, 0).toLocaleString()}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <Input
                placeholder="Search quotations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="no-uppercase"
              />
            </div>
            <div className="w-48">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Quotations List */}
      <div className="grid grid-cols-1 gap-6">
        {filteredQuotations.map((quote) => (
          <Card key={quote.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-6">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{quote.quote_number}</h3>
                    <Badge className={
                      quote.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                      quote.status === 'approved' ? 'bg-green-100 text-green-800' :
                      quote.status === 'rejected' ? 'bg-red-100 text-red-800' :
                      (quote.status === 'converted' || quote.status === 'invoiced') ? 'bg-indigo-100 text-indigo-800' :
                      'bg-gray-100 text-gray-800'
                    } data-testid={`quotation-status-badge-${quote.id}`}>
                      {(quote.status === 'converted' || quote.status === 'invoiced')
                        ? 'Converted'
                        : (quote.status || 'pending').charAt(0).toUpperCase() + (quote.status || 'pending').slice(1)}
                    </Badge>
                    {(quote.status === 'converted' || quote.status === 'invoiced') && quote.invoice_number && (
                      <span className="inline-flex items-center text-xs font-medium text-indigo-700 bg-indigo-50 px-2 py-1 rounded-full" data-testid={`quotation-linked-invoice-${quote.id}`}>
                        🔗 {quote.invoice_number}
                      </span>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Customer:</span> {quote.customer_name}
                    </div>
                    <div>
                      <span className="font-medium">Vehicle:</span> {quote.vehicle_info}
                    </div>
                    <div>
                      <span className="font-medium">Amount:</span> ₹{quote.total_amount.toLocaleString()}
                    </div>
                    <div>
                      <span className="font-medium">Created:</span> {new Date(quote.created_at).toLocaleDateString()}
                    </div>
                    <div>
                      <span className="font-medium">Valid Until:</span> {new Date(quote.valid_until).toLocaleDateString()}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2">
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleViewQuotation(quote)}
                  >
                    <Eye className="h-4 w-4 mr-1" />
                    View
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleEditQuotation(quote)}
                    disabled={quote.status === 'converted' || quote.status === 'invoiced'}
                    title={(quote.status === 'converted' || quote.status === 'invoiced') ? 'Converted quotations are locked' : 'Edit quotation'}
                    data-testid={`quotation-edit-btn-${quote.id}`}
                  >
                    <Edit className="h-4 w-4 mr-1" />
                    Edit
                  </Button>

                  {(quote.status === 'converted' || quote.status === 'invoiced') ? (
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => handleConvertToInvoice(quote)}
                      data-testid={`quotation-reconvert-btn-${quote.id}`}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Create Another Invoice
                    </Button>
                  ) : (
                    <Button
                      variant="default"
                      size="sm"
                      className="bg-indigo-600 hover:bg-indigo-700 text-white"
                      onClick={() => handleConvertToInvoice(quote)}
                      data-testid={`quotation-convert-btn-${quote.id}`}
                    >
                      <FileText className="h-4 w-4 mr-1" />
                      Convert to Invoice
                    </Button>
                  )}

                  {/* Status Change Buttons (hidden once converted/locked) */}
                  {(!quote.status || quote.status === 'pending') && (
                    <>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-green-600 border-green-600 hover:bg-green-50"
                        onClick={() => handleStatusChange(quote.id, 'approved')}
                        data-testid={`quotation-approve-btn-${quote.id}`}
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Approve
                      </Button>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="text-red-600 border-red-600 hover:bg-red-50"
                        onClick={() => handleStatusChange(quote.id, 'rejected')}
                        data-testid={`quotation-reject-btn-${quote.id}`}
                      >
                        <X className="h-4 w-4 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  
                  {quote.status === 'approved' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-orange-600 border-orange-600 hover:bg-orange-50"
                      onClick={() => handleStatusChange(quote.id, 'pending')}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Set Pending
                    </Button>
                  )}
                  
                  {quote.status === 'rejected' && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-orange-600 border-orange-600 hover:bg-orange-50"
                      onClick={() => handleStatusChange(quote.id, 'pending')}
                    >
                      <Clock className="h-4 w-4 mr-1" />
                      Set Pending
                    </Button>
                  )}
                  
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => handleGeneratePDF(quote)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    PDF
                  </Button>
                  
                  {/* Delete Button (disabled once converted) */}
                  <Button 
                    variant="outline" 
                    size="sm"
                    className="text-red-600 border-red-600 hover:bg-red-50"
                    disabled={quote.status === 'converted' || quote.status === 'invoiced'}
                    title={(quote.status === 'converted' || quote.status === 'invoiced') ? 'Converted quotations cannot be deleted' : 'Delete quotation'}
                    onClick={() => {
                      handleDeleteQuotation(quote);
                    }}
                    data-testid={`quotation-delete-btn-${quote.id}`}
                  >
                    <Trash2 className="h-4 w-4 mr-1" />
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredQuotations.length === 0 && (
        <div className="text-center py-12">
          <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-1">No quotations found</h3>
          <p className="text-sm text-gray-500">
            {searchTerm ? 'Try adjusting your search criteria.' : 'Create your first quotation to get started.'}
          </p>
        </div>
      )}

      {/* Add Quotation Modal */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Quotation</DialogTitle>
            <DialogDescription>
              Fill in the customer and vehicle details to create a new quotation for services.
            </DialogDescription>
          </DialogHeader>
          <AddQuotationForm 
            onQuotationAdded={handleQuotationAdded}
            onCancel={() => setShowAddDialog(false)}
            services={services}
            parts={parts}
          />
        </DialogContent>
      </Dialog>

      {/* View Quotation Modal */}
      <Dialog open={showViewDialog} onOpenChange={setShowViewDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>View Quotation - {selectedQuotation?.quote_number}</DialogTitle>
            <DialogDescription>
              Review the complete quotation details including customer information, services, and pricing.
            </DialogDescription>
          </DialogHeader>
          {selectedQuotation && (
            <ViewQuotationDetails 
              quotation={selectedQuotation}
              onClose={() => setShowViewDialog(false)}
              onRecordAdvance={(quotation) => {
                setSelectedQuotation(quotation);
                setShowAdvancePaymentDialog(true);
              }}
              onGenerateReceipt={generateAdvanceReceipt}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Quotation Modal */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Quotation - {selectedQuotation?.quote_number}</DialogTitle>
            <DialogDescription>
              Update the quotation details including services, pricing, and customer information.
            </DialogDescription>
          </DialogHeader>
          {selectedQuotation && (
            <EditQuotationForm 
              quotation={selectedQuotation}
              onQuotationUpdated={handleQuotationUpdated}
              onCancel={() => {
                setShowEditDialog(false);
                setSelectedQuotation(null);
              }}
              services={services}
              parts={parts}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Advance Payment Dialog */}
      <Dialog open={showAdvancePaymentDialog} onOpenChange={setShowAdvancePaymentDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Record Advance Payment</DialogTitle>
            <DialogDescription>
              Record advance payment for {selectedQuotation?.quote_number}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Payment Amount Options */}
            <div>
              <Label className="text-base font-medium">Payment Amount</Label>
              <div className="mt-2 space-y-3">
                {/* Fixed Amount Option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="fixed-amount"
                    name="amount-type"
                    checked={!advancePaymentData.is_percentage}
                    onChange={() => setAdvancePaymentData(prev => ({ ...prev, is_percentage: false, percentage: '' }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="fixed-amount">Fixed Amount</Label>
                </div>
                {!advancePaymentData.is_percentage && (
                  <div className="ml-6">
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Enter amount (₹)"
                      value={advancePaymentData.amount}
                      onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, amount: e.target.value }))}
                    />
                  </div>
                )}

                {/* Percentage Option */}
                <div className="flex items-center space-x-2">
                  <input
                    type="radio"
                    id="percentage-amount"
                    name="amount-type"
                    checked={advancePaymentData.is_percentage}
                    onChange={() => setAdvancePaymentData(prev => ({ ...prev, is_percentage: true, amount: '' }))}
                    className="h-4 w-4"
                  />
                  <Label htmlFor="percentage-amount">Percentage of Total</Label>
                </div>
                {advancePaymentData.is_percentage && (
                  <div className="ml-6">
                    <div className="flex items-center space-x-2">
                      <Input
                        type="number"
                        min="1"
                        max="100"
                        placeholder="Enter percentage"
                        value={advancePaymentData.percentage}
                        onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, percentage: e.target.value }))}
                        className="flex-1"
                      />
                      <span className="text-sm text-gray-500">%</span>
                    </div>
                    {advancePaymentData.percentage && selectedQuotation && (
                      <p className="text-sm text-blue-600 mt-1">
                        Amount: ₹{((selectedQuotation.total_amount * parseFloat(advancePaymentData.percentage || 0)) / 100).toLocaleString('en-IN')}
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Payment Method */}
            <div>
              <Label htmlFor="payment-method">Payment Method</Label>
              <Select 
                value={advancePaymentData.payment_method} 
                onValueChange={(value) => setAdvancePaymentData(prev => ({ ...prev, payment_method: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="cash">Cash</SelectItem>
                  <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                  <SelectItem value="upi">UPI Payment</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Bank Account Selection (for Bank Transfer) */}
            {advancePaymentData.payment_method === 'bank_transfer' && (
              <div>
                <Label htmlFor="quotation-bank-account">Select Bank Account *</Label>
                <Select 
                  value={advancePaymentData.bank_account_id} 
                  onValueChange={(value) => setAdvancePaymentData(prev => ({ ...prev, bank_account_id: value }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose bank account" />
                  </SelectTrigger>
                  <SelectContent>
                    {bankAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.bank_name} - {account.account_number.replace(/(.{4})/g, '$1 ').trim()}
                        <div className="text-xs text-gray-500">
                          Balance: ₹{(account.current_balance || 0).toLocaleString('en-IN')}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {advancePaymentData.payment_method === 'bank_transfer' && !advancePaymentData.bank_account_id && (
                  <p className="text-sm text-red-500 mt-1">Bank account selection is required</p>
                )}
              </div>
            )}

            {/* Reference Number (for Bank/UPI) */}
            {(advancePaymentData.payment_method === 'bank_transfer' || advancePaymentData.payment_method === 'upi') && (
              <div>
                <Label htmlFor="reference">Reference Number</Label>
                <Input
                  id="reference"
                  placeholder="Enter transaction reference number"
                  value={advancePaymentData.reference_number}
                  onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, reference_number: e.target.value }))}
                />
              </div>
            )}

            {/* Notes */}
            <div>
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                placeholder="Additional notes"
                value={advancePaymentData.notes}
                onChange={(e) => setAdvancePaymentData(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>

            {/* Payment Summary */}
            {selectedQuotation && (
              <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Total Quote:</span>
                    <span>₹{selectedQuotation.total_amount?.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Already Received:</span>
                    <span>₹{(selectedQuotation.total_advance_received || 0).toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between font-semibold text-blue-700 border-t border-blue-200 pt-1">
                    <span>Remaining Balance:</span>
                    <span>₹{(selectedQuotation.total_amount - (selectedQuotation.total_advance_received || 0)).toLocaleString('en-IN')}</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end space-x-2 mt-6">
            <Button 
              variant="outline" 
              onClick={() => {
                setShowAdvancePaymentDialog(false);
                setAdvancePaymentData({
                  amount: '',
                  payment_method: 'cash',
                  reference_number: '',
                  notes: '',
                  percentage: '',
                  is_percentage: false
                });
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={handleAdvancePayment}
              className="bg-green-600 hover:bg-green-700"
            >
              Record Payment
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Small UI component to render a percentage-change badge inside an accounting card.
// Props:
//   pct: number | null  — percentage change (can be negative). null means "no comparison possible" (previous was 0).
//   prevValue: number   — the previous-period absolute value (shown in tooltip / subtext).
//   higherIsBetter: bool — true for revenue/profit, false for expenses (controls color).
// [AccountingSection extracted to pages/AccountingSection.jsx]

// Enhanced Add Problem Form for the new vehicle problems system

export { QuotationsSection };
